import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import AdminLayout from '@/layouts/AdminLayout'
import { authService } from '@services/api'
import { useAuthStore } from '@store'

const storageURL = import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage'
const mediaUrl = (p) => { if (!p) return null; let s = String(p); const i = s.indexOf('/storage/'); if (i !== -1) s = s.slice(i + 9); return storageURL + '/' + s.replace(/^\/+/, '') }
const getPhotoUrl = (p) => !p ? null : p.startsWith('http') ? p : mediaUrl(p)

function AdminProfilePage() {
  const { user, updateUser } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [adminData, setAdminData] = useState(null)
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', phone: '',
    location: '', website: '', bio: '', photo: '', role: ''
  })

  const applyAdminData = (data) => {
    setAdminData(data)
    const mainRole = data.role?.name || (Array.isArray(data.roles) ? data.roles[0]?.name || data.roles[0] || '' : '')
    setFormData({
      nom:      data.nom      || '',
      prenom:   data.prenom   || '',
      email:    data.email    || '',
      phone:    data.phone    || data.numero || '',
      location: data.location || data.adresse || '',
      website:  data.website  || data.site_web || '',
      bio:      data.bio      || '',
      photo:    getPhotoUrl(data.photo_profil || data.photo) || '',
      role:     mainRole,
    })
  }

  useEffect(() => {
    const loadProfile = async () => {
      // 1. Afficher immédiatement ce qu'on a (store ou localStorage)
      const stored = user || (() => {
        try {
          const raw = localStorage.getItem('kudhi_user') || localStorage.getItem('admin_user') || localStorage.getItem('user')
          return raw ? JSON.parse(raw) : null
        } catch { return null }
      })()
      if (stored) applyAdminData(stored)

      // 2. Rafraîchir depuis l'API
      try {
        const res = await authService.getProfile()
        if (res?.success !== false && res?.data) {
          applyAdminData(res.data)
          if (updateUser) updateUser(res.data)
        }
      } catch (e) {
        console.error('Erreur chargement profil API:', e)
      }
    }
    loadProfile()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image')
      return
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image est trop volumineuse (max 5MB)')
      return
    }

    setUploadingPhoto(true)
    try {
      // Le backend identifie l'admin via le token JWT — pas besoin d'user_id
      const data = await authService.uploadProfilePhoto(file)
      if (data?.success) {
        const newPhotoUrl = getPhotoUrl(data.photo_url) || data.photo_url
        setFormData(prev => ({ ...prev, photo: newPhotoUrl }))
        const updatedAdmin = { ...adminData, photo_profil: data.photo_url, photo: newPhotoUrl }
        setAdminData(updatedAdmin)
        if (updateUser) updateUser(updatedAdmin)
        toast.success('Photo de profil mise à jour !')
      } else {
        toast.error(data?.message || 'Erreur lors de l\'upload')
      }
    } catch (error) {
      console.error('Erreur upload photo:', error)
      toast.error('Erreur lors de l\'upload de la photo')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Le backend identifie l'admin via le token JWT — pas besoin d'user_id
      const data = await authService.updateProfile({
        nom:      formData.nom,
        prenom:   formData.prenom,
        phone:    formData.phone,
        location: formData.location,
        website:  formData.website,
        bio:      formData.bio,
      })

      if (data?.success) {
        const fresh = data.data || data
        const updatedAdmin = { ...adminData, ...fresh }
        setAdminData(updatedAdmin)
        if (updateUser) updateUser(updatedAdmin)
        applyAdminData(updatedAdmin)
        toast.success('Profil mis à jour avec succès !')
        setIsEditing(false)
      } else {
        toast.error(data?.message || 'Erreur lors de la mise à jour')
      }
    } catch (error) {
      console.error('Erreur mise à jour profil:', error)
      const msg = error?.response?.data?.message
        || error?.response?.data?.errors && Object.values(error.response.data.errors).flat().join(', ')
        || error?.message
        || 'Erreur de connexion au serveur'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const calculateProfileCompletion = () => {
    const fields = [formData.nom, formData.prenom, formData.email, formData.phone, formData.location, formData.website, formData.bio, formData.photo]
    const filledFields = fields.filter(field => field && field.trim() !== '').length
    return Math.round((filledFields / fields.length) * 100)
  }

  const getRoleColor = (role) => {
    const colors = {
      'Super Admin': 'from-purple-500 to-purple-700',
      'Admin': 'from-blue-500 to-blue-700',
      'Superviseur': 'from-green-500 to-green-700',
      'Animateur': 'from-yellow-500 to-yellow-700',
      'Technicien': 'from-orange-500 to-orange-700'
    }
    return colors[role] || 'from-gray-500 to-gray-700'
  }

  if (!adminData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#D4AF37] mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Chargement du profil...</p>
        </div>
      </div>
    )
  }

  return (
    <AdminLayout>
      <div className="w-full mx-auto mt-10">
        {/* <div className="mb-8">
          <h1 className="text-4xl font-bold text-gradient-ligdi mb-2">Mon Profil Administrateur</h1>
          <p className="text-gray-600">Gérez vos informations personnelles et votre compte administrateur</p>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <div className="card-ligdi text-center">
              <div className="relative w-32 h-32 mx-auto mb-4">
                {formData.photo ? (
                  <>
                    <img
                      src={formData.photo}
                      alt=""
                      className="w-full h-full rounded-full object-cover shadow-xl absolute inset-0"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                    <div
                      className="w-full h-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-full items-center justify-center text-white text-5xl font-bold shadow-xl"
                      style={{ display: 'none' }}
                    >
                      {(formData.prenom?.charAt(0) || formData.nom?.charAt(0) || 'A').toUpperCase()}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-xl">
                    {(formData.prenom?.charAt(0) || formData.nom?.charAt(0) || 'A').toUpperCase()}
                  </div>
                )}
                <input 
                  type="file" 
                  id="photo-upload" 
                  accept="image/*" 
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <label 
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors border-2 border-[#D4AF37] cursor-pointer"
                >
                  {uploadingPhoto ? (
                    <svg className="animate-spin w-5 h-5 text-[#D4AF37]" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </label>
              </div>
              <h3 className="text-2xl font-bold text-[#001F3F] mb-2">{`${formData.prenom || ''} ${formData.nom || ''}`.trim() || 'Administrateur'}</h3>
              <p className="text-sm text-gray-500 mb-3">{formData.email}</p>
              
              {formData.role && (
                <div className="mb-4">
                  <span className={`inline-block px-4 py-2 rounded-full text-white font-semibold bg-gradient-to-r ${getRoleColor(formData.role)} shadow-md`}>
                    <svg className="w-4 h-4 inline-block mr-1 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    {formData.role}
                  </span>
                </div>
              )}

              {formData.location && (
                <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mb-4">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {formData.location}
                </div>
              )}
              
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">Membre depuis</div>
                <div className="font-semibold text-[#001F3F]">{new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</div>
              </div>
            </div>

            <div className="card-ligdi">
              <h3 className="text-lg font-semibold text-[#001F3F] mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Profil complet
              </h3>
              <div className="mb-3">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 font-medium">Progression</span>
                  <span className="text-[#D4AF37] font-bold text-lg">{calculateProfileCompletion()}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] h-3 rounded-full transition-all duration-500 shadow-inner" 
                    style={{ width: `${calculateProfileCompletion()}%` }}
                  ></div>
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.nom && formData.prenom ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.nom && formData.prenom ? 'text-gray-700' : 'text-gray-400'}>Nom complet</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.email ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.email ? 'text-gray-700' : 'text-gray-400'}>Email</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.phone ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.phone ? 'text-gray-700' : 'text-gray-400'}>Téléphone</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.location ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.location ? 'text-gray-700' : 'text-gray-400'}>Localisation</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.bio ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.bio ? 'text-gray-700' : 'text-gray-400'}>Bio</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                💡 Un profil complet améliore la communication avec votre équipe
              </p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={handleSubmit} className="card-ligdi">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-[#001F3F] flex items-center gap-2">
                    <svg className="w-6 h-6 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Informations personnelles
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">Gérez vos informations de contact et votre présentation</p>
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors flex items-center gap-2 shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Modifier
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Prénom
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all"
                      placeholder="Votre prénom"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.prenom || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Nom
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all"
                      placeholder="Votre nom"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.nom || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all"
                      placeholder="votre@email.com"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.email || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Téléphone
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all"
                      placeholder="+221 77 123 45 67"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.phone || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Localisation
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all"
                      placeholder="Dakar, Sénégal"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.location || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Site web
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all"
                      placeholder="https://votresite.com"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium">
                        {formData.website ? (
                          <a href={formData.website} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline flex items-center gap-1">
                            {formData.website}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Non renseigné</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false)
                      // Réinitialiser les données
                      if (adminData) {
                        // Déterminer le rôle principal
                        let mainRole = ''
                        if (Array.isArray(adminData.roles)) {
                          mainRole = adminData.roles[0] || ''
                        } else if (typeof adminData.roles === 'string') {
                          const rolesArray = adminData.roles.split(',')
                          mainRole = rolesArray[0] || ''
                        }
                        
                        setFormData({
                          nom: adminData.nom || '',
                          prenom: adminData.prenom || '',
                          email: adminData.email || '',
                          phone: adminData.numero || adminData.phone || '',
                          location: adminData.adresse || adminData.location || '',
                          website: adminData.site_web || adminData.website || '',
                          bio: adminData.bio || '',
                          photo: adminData.photo_profil || adminData.photo || '',
                          role: mainRole
                        })
                      }
                    }}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Enregistrer les modifications
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>

            <div className="card-ligdi">
              <h2 className="text-2xl font-bold text-[#001F3F] mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                Bio
              </h2>
              {isEditing ? (
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-all"
                  placeholder="Parlez-nous de vous, de votre rôle dans l'équipe, de vos responsabilités..."
                />
              ) : (
                <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[120px]">
                  <p className="text-gray-700 whitespace-pre-wrap">{formData.bio || <span className="text-gray-400 italic">Aucune description disponible. Ajoutez une bio pour que votre équipe puisse mieux vous connaître.</span>}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminProfilePage
